/**
 * Video practice (completion plan P5).
 *
 * ── WHAT VIDEO ADDS, AND WHAT IT DOES NOT ──────────────────────────────────────
 *
 * The conversation is the SAME canonical realtime conversation Live
 * Conversation holds: same persona, scenario, runtime, Development/Testing
 * behaviour. The attempt is created as `realtime_audio_video`, so the modality
 * is frozen into its binding and the persona is told it cannot see the learner.
 *
 * The camera is a local preview only. The stream is attached to a <video>
 * element and nothing else: it is not recorded, not uploaded, not sampled into
 * frames, and not sent to the conversation model. Visual measurement belongs to
 * LevelUp Media; until it exists the observable video dimensions are reported
 * "Not measured — awaiting LevelUp Media".
 *
 * ── ORDER OF OPERATIONS ────────────────────────────────────────────────────────
 *
 * consent → camera permission → Start (creates the attempt, opens the mic).
 * Choosing the Video tab creates nothing and requests nothing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { LiveConversationPanel } from '../LiveConversationPanel';
import { VideoConsent } from './VideoConsent';

type CameraState = 'off' | 'requesting' | 'on' | 'denied' | 'unavailable';

const CAMERA_MESSAGE: Partial<Record<CameraState, string>> = {
  denied: 'Camera access was blocked. Allow it in your browser settings, or use Live Conversation without video.',
  unavailable: 'No camera was found. You can still use Live Conversation or Written Chat.',
};

/** The local camera preview. Owns the stream; stops every track when done. */
function useCameraPreview() {
  const [state, setState] = useState<CameraState>('off');
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setState('off');
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setState('unavailable');
      return;
    }
    setState('requesting');
    try {
      // Video only: the realtime client asks for the microphone itself when
      // the conversation starts, exactly as Live Conversation does.
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        void videoRef.current.play().catch(() => undefined);
      }
      setState('on');
    } catch (err) {
      const name = (err as { name?: string })?.name;
      setState(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable');
    }
  }, []);

  // Leaving the tab or the page turns the camera off.
  useEffect(() => stop, [stop]);

  // The element may mount after the stream exists.
  useEffect(() => {
    if (state === 'on' && videoRef.current && streamRef.current && videoRef.current.srcObject !== streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [state]);

  return { state, start, stop, videoRef };
}

export interface VideoPracticePanelProps {
  wsUrl: string;
  token: string | null | undefined;
  /** Create the video attempt (`realtime_audio_video`). Called on Start only. */
  beginAttempt: () => Promise<string | null>;
  personaName: string;
  /** Server capability `conversation.video`. Never a local flag. */
  available: boolean;
  mode: 'development' | 'testing';
  /** The attempt ended (End or server completion): once, with its session id. */
  onEnd?: (sessionId: string) => void;
}

export function VideoPracticePanel({
  wsUrl, token, beginAttempt, personaName, available, mode, onEnd,
}: VideoPracticePanelProps) {
  const [consented, setConsented] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const camera = useCameraPreview();

  if (!available) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        Video practice isn’t available right now. Live Conversation and Written Chat still are.
      </div>
    );
  }

  if (!confirmed) {
    return (
      <VideoConsent
        consented={consented}
        onConsentChange={setConsented}
        mode={mode}
        onContinue={() => {
          if (!consented) return;
          setConfirmed(true);
          void camera.start();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="relative overflow-hidden rounded-xl bg-gray-900 aspect-video max-w-md w-full mx-auto">
        <video
          ref={camera.videoRef}
          autoPlay
          playsInline
          muted
          aria-label="Your camera preview (not recorded)"
          className="h-full w-full object-cover -scale-x-100"
        />
        {camera.state !== 'on' && (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-gray-200">
            {camera.state === 'requesting' ? 'Waiting for camera permission…'
              : CAMERA_MESSAGE[camera.state] ?? 'Camera is off.'}
          </div>
        )}
        <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">
          Preview only · not recorded
        </span>
      </div>

      {(camera.state === 'denied' || camera.state === 'unavailable') && (
        <button
          type="button"
          onClick={() => void camera.start()}
          className="self-center text-sm text-primary-700 underline"
        >
          Try the camera again
        </button>
      )}

      {camera.state === 'on' && (
        <LiveConversationPanel
          wsUrl={wsUrl}
          token={token}
          beginAttempt={beginAttempt}
          personaName={personaName}
          available={available}
          onEnd={(sessionId) => {
            camera.stop();
            onEnd?.(sessionId);
          }}
        />
      )}
    </div>
  );
}

export default VideoPracticePanel;
